import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IntlContext } from "context";
import FieldGroup from "../Element/FieldGroup";
import Label from "../Element/Label";
import Adornment from "../Element/Adornment";
import Feedback from "../Element/Feedback";
import Input from "../Select/Input";
import Checkbox from "../Element/Checkbox";
import { useFormState } from "react-final-form";

export interface SelectSearchProps extends SelectProps {
    minSearch?: number;
}

export interface FormAutocompleteSelectCheckboxProps extends FormBaseCheckboxProps {
    inputProps: SelectSearchProps;
}

const FormSelectCheckbox: React.FC<FormAutocompleteSelectCheckboxProps> = ({
    error,
    errorMessage,
    infoMessage,
    labelProps,
    inputProps,
    fieldGroupProps,
    fieldControlProps: fieldControlPropsExtended,
    adornmentStart,
    adornmentEnd,
    listHeader,
    adormentRight,
    displaySelected = false,
}) => {
    const intl = useContext(IntlContext);
    const disabled = inputProps.disabled;
    const required = inputProps.required;
    const minSearch = inputProps?.minSearch ?? 2;
    const { values } = useFormState();
    const wrapperListRef = useRef(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>("");
    const selectedItem = inputProps.options.find((item) => item.value === inputProps.value);

    const fieldControlProps = Object.assign(
        {
            className: "field-control field-control__select-wrapper",
            errorClassName: "error",
            disabledClassName: "disabled",
        },
        fieldControlPropsExtended
    );

    const openMenu = useCallback(() => {
        return setIsOpen(true);
    }, []);

    const onFocus = useCallback(() => {
        return setIsOpen(true);
    }, []);

    if (
        inputProps.title === undefined &&
        labelProps !== undefined &&
        labelProps.label !== undefined &&
        labelProps.label !== null
    ) {
        inputProps.title = labelProps.label.toString();
    }

    const onSearch = (event: any) => {
        setSearchString(event.target.value ?? "");
    }

    const filterNothing = (value: any) => {
        return true;
    }

    const filterSearch = (value: OptionsSelectCheckBoxProps) => {
        if (searchString.length < minSearch) {
            return true;
        }
        const safeString = normalizeString(searchString).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`(?:[^\\s\\(]*)\\s*\\(?${safeString}\\)?`, 'i');
        return regex.test(normalizeString(value.label));
    }

    // Normalisation de la chaine et suppression des accents
    const normalizeString = (str: string) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    };

    useEffect(() => {
        const hideWidget = () => {
            setSearchString("");
            setIsOpen(false);
        };

        const handleClickOutside = (event: MouseEvent) => {
            if (!(wrapperListRef.current as any)?.contains(event.target)) {
                hideWidget();
            }
        };
        document.addEventListener("mousedown", handleClickOutside, true);
        return () => {
            hideWidget();
        };
    }, []);

    const handleOnChangeCheckbox = useCallback(
        (e: any) => {
            inputProps.onChange && inputProps.onChange(e);
        },
        [inputProps]
    );

    const displaySelectedElements = () => {
        if (!displaySelected || disabled) {
            return false;
        }
        if (!Array.isArray(inputProps?.value)) {
            return false;
        }
        if (inputProps?.value.length === 0) {
            return false;
        }
        return true;
    };

    const renderElement = () => {
        let filtered = inputProps.options.filter(searchString.length !== 0 ? filterSearch : filterNothing);

        return filtered.length === 0
            ? [<li
                    key={0}
                    className="field-control-select__item field-control-select__item-stats"
                >
                    <div className="field-control-checkbox__group field-control-checkbox__group-stats">
                        <label className="field-control-checkbox__label">
                            {intl.formatMessage("produit.label.no_result")}
                        </label>
                    </div>
                </li>]
            : filtered.map((item, index) => (
            <li
                key={index + "_" + item.value}
                className="field-control-select__item field-control-select__item-stats"
            >
                <Checkbox
                    className={"field-control-checkbox__input field-control-checkbox__input-stats"}
                    groupClassName={"field-control-checkbox__group field-control-checkbox__group-stats"}
                    name={inputProps.name}
                    options={[{ label: item.label, value: item.value }]}
                    onChange={handleOnChangeCheckbox}
                    fields={{
                        value: Array.isArray(values?.[inputProps.name]) ? values[inputProps.name] : [],
                    }}
                />
            </li>
        ));
    }

    return (
        <FieldGroup error={error} {...fieldGroupProps}>
            {labelProps !== undefined && (
                <Label htmlFor={inputProps.id} disabled={disabled} error={error} required={required} {...labelProps}>
                    {labelProps.label}
                </Label>
            )}

            <div className="field-control-wrapper field-control-wrapper-stats" ref={wrapperListRef}>
                <div
                    className={`${fieldControlProps.className} ${disabled ? fieldControlProps.disabledClassName : ""} ${
                        error ? fieldControlProps.errorClassName : ""
                    }`}
                >
                    {adornmentStart !== undefined && <Adornment position="start">{adornmentStart}</Adornment>}
                    <div className={inputProps.className || "field-control-select" + (isOpen ? " is-open" : "")}>
                        <Input
                            {...{
                                id: inputProps.id,
                                name: inputProps.name,
                                placeholder: selectedItem
                                    ? undefined
                                    : inputProps.placeholder ||
                                      intl.formatMessage("generic.form.select_custom.placeholder"),
                                autoFocus: inputProps.autofocus,
                                disabled: inputProps.disabled,
                                readOnly: inputProps.readOnly,
                                onFocus: onFocus,
                                onMouseDown: openMenu,
                                onChange: onSearch,
                                title: inputProps.title ?? "",
                                value: searchString,
                                ...inputProps.searchInputProps,
                            }}
                        />
                    </div>

                    {adornmentEnd !== undefined && <Adornment position="end">{adornmentEnd}</Adornment>}

                    {(searchString.length === 0) && listHeader && (
                        <div
                            className="field-control-select__list-select-all"
                            style={{ display: isOpen ? "initial" : "none" }}
                        >
                            {listHeader}
                        </div>
                    )}

                    <ul
                        className="field-control-select__list field-control-select__list-stats"
                        style={{ display: isOpen ? "initial" : "none" }}
                    >
                        {renderElement()}
                    </ul>
                </div>
                {adormentRight}
            </div>

            {displaySelectedElements() && (
                <Feedback className="field__feedback selected">
                    {intl.formatMessage(
                        typeof displaySelected === "boolean" ? "generic.selected_items" : displaySelected,
                        { items: Array.isArray(inputProps?.value) ? inputProps?.value.length : 0 }
                    )}
                </Feedback>
            )}
            {infoMessage !== undefined && !disabled && <Feedback className="field__feedback">{infoMessage}</Feedback>}
            {error && errorMessage !== undefined && (
                <Feedback className="field__feedback error">{errorMessage}</Feedback>
            )}
        </FieldGroup>
    );
};

export default FormSelectCheckbox;
